/**
 * brand — PAROLE DE VÉRITÉ UNIQUE sur ce qu'est Rectorium.
 *
 * Décision d'Alfred (2026-07-27) : une seule phrase décrit le produit. Pour
 * qu'elle le reste, une seule CONSTANTE la porte — toute surface JS/JSX qui
 * décrit Rectorium doit IMPORTER d'ici, jamais recopier la phrase. C'est ce qui
 * empêche la dérive : plusieurs copies finissent toujours par diverger (le
 * dépôt en portait déjà plusieurs, dont « Plateforme de Gouvernance
 * Académique », désormais obsolète).
 *
 * Certains emplacements ne PEUVENT PAS importer ce module, faute de résolution
 * vers `src/` ou parce qu'ils ne sont pas du JavaScript :
 *   - `index.html` (métadonnées et `<title>`) ;
 *   - `package.json` (champ `description`) ;
 *   - les fichiers `.md` et `.html` de documentation ;
 *   - `supabase/functions/send-email/index.ts` — fonction Deno déployée
 *     isolément, qui ne peut pas atteindre `src/` et en garde donc un MIROIR.
 * Ils sont tenus synchronisés par le test de garde
 * `src/__tests__/brandSignature.test.js`, qui échoue si l'un d'eux s'écarte de
 * la constante ou si une ancienne formule réapparaît quelque part.
 *
 * `vite.config.js`, en revanche, IMPORTE bien ce module : le manifest PWA est
 * généré à la compilation, il n'a donc aucune copie à tenir à jour.
 *
 * Règle de discernement, à respecter en l'utilisant : cette signature décrit
 * RECTORIUM. Sur une surface qui appartient à un établissement client (hero de
 * son portail, `welcome_message`, branding, pied de page de ses documents),
 * l'identité de l'établissement prime — on n'y met au plus que
 * `RECTORIUM_POWERED_BY`.
 */

/** Le nom du produit, seul. */
export const RECTORIUM_NAME = 'Rectorium OS';

/**
 * Mentions institutionnelles et légales de Rectorium Corporation (Delaware, USA).
 */
export const RECTORIUM_COMPANY = 'Rectorium Corporation';
export const RECTORIUM_COMPANY_NAME = 'Rectorium Corporation';
export const RECTORIUM_COMPANY_LOCATION = "État du Delaware, USA";
export const RECTORIUM_COMPANY_LOCATION_EN = 'State of Delaware, USA';
export const RECTORIUM_LEGAL_NOTICE =
  "Rectorium OS, une marque mondiale de Rectorium Corporation, basée dans l'État du Delaware, USA.";
export const RECTORIUM_LEGAL_NOTICE_FR = RECTORIUM_LEGAL_NOTICE;
export const RECTORIUM_LEGAL_NOTICE_EN =
  'Rectorium OS, a global brand of Rectorium Corporation, based in the State of Delaware, USA.';

/**
 * Retourne la mention légale traduite ou la valeur par défaut.
 * @param {Function} [t] - fonction de traduction i18next (useTranslation)
 */
export const getRectoriumLegalNotice = (t) =>
  (t ? t('constants.brand.legal_notice', { defaultValue: RECTORIUM_LEGAL_NOTICE }) : RECTORIUM_LEGAL_NOTICE);

/**
 * Ce que Rectorium OS EST, en une phrase. Signature complète et canonique :
 * la seule description d'une ligne autorisée dans le produit.
 */
export const RECTORIUM_SIGNATURE =
  'Rectorium OS — Plateforme de gouvernance institutionnelle et de pilotage académique';

/**
 * La même chose sans le nom — pour les endroits où « Rectorium OS » est déjà
 * affiché juste à côté (sous un logo, après un titre), afin de ne pas répéter
 * le nom deux fois dans la même phrase.
 */
export const RECTORIUM_DESCRIPTION =
  'Plateforme de gouvernance institutionnelle et de pilotage académique';

/**
 * Forme courte, réservée aux emplacements physiquement trop étroits pour la
 * phrase entière (onglet de navigateur, badge, colonne de tableau). Ne pas
 * l'employer par simple commodité : la forme complète est la référence.
 */
export const RECTORIUM_SHORT = 'Gouvernance institutionnelle & pilotage académique';

/**
 * Mention d'appartenance, sur une surface d'établissement client où la
 * signature complète serait déplacée.
 */
const RECTORIUM_POWERED_BY = 'Propulsé par Rectorium OS';

/**
 * Version traduite de RECTORIUM_POWERED_BY, pour affichage UI (ex. aperçu de
 * branding d'établissement, à défaut de slogan propre saisi). Contrairement à
 * RECTORIUM_NAME/RECTORIUM_SIGNATURE/RECTORIUM_DESCRIPTION/RECTORIUM_SHORT —
 * la « parole de vérité unique » sur ce qu'est Rectorium, volontairement figée
 * dans une seule langue par décision d'Alfred pour empêcher toute dérive —
 * cette mention d'appartenance n'est PAS une formule de positionnement : le
 * nom « Rectorium » reste non traduit, seule la préposition l'est, injectée
 * par interpolation pour rester grammaticalement correcte dans les deux
 * langues (« Propulsé par Rectorium » / « Powered by Rectorium »).
 * @param {Function} t - fonction de traduction i18next (useTranslation)
 */
export const getRectoriumPoweredBy = (t) =>
  t('constants.brand.powered_by', { name: RECTORIUM_NAME });

/**
 * Formules ANTÉRIEURES, devenues fausses ou concurrentes. Le test de garde
 * refuse leur réapparition dans le code et les surfaces publiques : c'est ce
 * qui rend la parole de vérité effectivement unique, et pas seulement déclarée.
 */
export const RECTORIUM_OBSOLETE_DESCRIPTIONS = [
  'Plateforme de Gouvernance Académique',
  'Plateforme de gouvernance académique',
  'Rectorium — Gouvernance Académique',
  "Solution intégrée de gestion et de gouvernance des établissements d'enseignement supérieur",
  'Digital Operating System for Universities and Training Institutions',
  'Digital Operating System pour les universités',
  "Système d'opération universitaire",
  'Système de gouvernance académique',
  'Academic governance system',
  'plateforme académique tout-en-un',
  'ERP Rectorium',
];

/**
 * ANCIEN NOM DE PRODUIT — Rectorium s'est appelé ainsi avant son changement de
 * marque (confirmé par Alfred, 2026-09-03).
 *
 * Il ne subsiste QUE dans cette constante, et c'est délibéré : une garde doit
 * nommer ce qu'elle interdit, sinon elle n'interdit rien. Ce fichier est la
 * seule place légitime — il porte déjà les formules obsolètes EN DONNÉES, et il
 * est à ce titre exclu du balayage (voir EXCLUDE_PATTERNS dans
 * brandSignature.test.js). Le test consomme cette constante au lieu d'écrire le
 * mot lui-même, de sorte qu'il n'apparaisse plus nulle part ailleurs.
 *
 * Comparaison en minuscules côté test : la variante en nom de domaine
 * échappait à une recherche sensible à la casse.
 */
export const RECTORIUM_ANCIEN_NOM_PRODUIT = 'cdp' + 'connect';
